import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FormTextInput } from 'components';
import { makeStyles } from '@material-ui/styles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import FiberManualRecord from '@material-ui/icons/FiberManualRecord';
import Divider from '@material-ui/core/Divider';
import { get, put } from 'helpers/apiHelpers';
import { toast } from 'react-toastify';
import { apiErrorToast } from 'utils/apiError';

import { DialogLoader } from '../../../components/DialogLoader';
import FormControlButtons from '../../../components/FormControlButtons/FormControlButtons';
import CardWrapper from '../../../components/Card/CardWrapper';
import GridItem from '../../../components/Grid/GridItem';
import GridContainer from '../../../components/Grid/GridContainer';
import CardBody from '../../../components/Card/CardBody';
import RegularCheckbox from '../../../components/Checkbox/RegularCheckbox';
import InfoIconTooltip from '../../../components/InfoIconTooltip/InfoIconTooltip';
import buttonsStyle from '../../../assets/jss/material-dashboard-pro-react/views/buttonsStyle';
import extendedFormsStyle from '../../../assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import { BackgroundSettingsFields } from '../clientPanelDesing/components/BackgroundSettings';
import { BannerSettingsFields } from '../orderForm/components/BannerSettings';
import {
  buildAccessKeyPayload,
  buildAppearanceState,
  getEffectiveAppMode,
  isShopOnlyAppMode,
  shouldPrefillAppearance,
} from './AccessKeyEditModule.utils';

const useStyles = makeStyles({
  ...extendedFormsStyle,
  ...buttonsStyle,
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  overrideWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  radioWrapper: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '30px',
  },
  cardTitleOverride: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: 'auto',
    paddingRight: '15px',
  },
  bannerFields: {
    marginTop: '30px',
  },
  divider: {
    marginBottom: '20px',
  },
});

const AccessKeyEditModule = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const selectedBrand = useSelector(state => state.Auth.selectedBrand);
  const [appMode, setAppMode] = useState(null);
  const [brandAppMode, setBrandAppMode] = useState({});
  const [brandAppearance, setBrandAppearance] = useState({
    BrandColors: {},
    OrderFormBanner: {},
  });
  const [keyAppearance, setKeyAppearance] = useState({
    BrandColors: null,
    OrderFormBanner: null,
  });
  const [isAppearanceOverridden, setIsAppearanceOverridden] = useState(false);
  const [isAppearancePrefilled, setIsAppearancePrefilled] = useState(false);
  const [isColor, setIsColor] = useState(true);
  const [accessKeyName, setAccessKeyName] = useState('');
  const [domain, setDomain] = useState('');
  const [applePayDomainAssociation, setApplePayDomainAssociation] =
    useState('');
  const [
    initialApplePayDomainAssociation,
    setInitialApplePayDomainAssociation,
  ] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // eslint-disable-next-line no-use-before-define -- FIXME
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  const fetchData = async () => {
    try {
      const [response, brandModulesResponse] = await Promise.all([
        get(`/brand-access-keys/${id}`),
        get(`/brands/${selectedBrand}/modules`, {
          module: ['AppMode', 'BrandColors', 'OrderFormBanner'],
        }),
      ]);
      const keyModules = response?.modulesConfiguration ?? {};
      const brandModules = brandModulesResponse?.configuration ?? {};
      const {
        hasAppearanceOverride,
        brandAppearance: brandAppearanceModules,
        keyAppearance: keyAppearanceModules,
      } = buildAppearanceState(keyModules, brandModules);

      setAppMode(keyModules.AppMode ?? {});
      setBrandAppMode(brandModules.AppMode ?? {});
      setBrandAppearance(brandAppearanceModules);
      setKeyAppearance(keyAppearanceModules);
      setIsAppearanceOverridden(hasAppearanceOverride);
      setIsAppearancePrefilled(hasAppearanceOverride);
      setAccessKeyName(response?.name ?? '');
      setDomain(response?.domain ?? '');
      const applePayValue =
        response?.modulesConfiguration?.PayU?.applePayDomainAssociation ?? '';
      setApplePayDomainAssociation(applePayValue);
      setInitialApplePayDomainAssociation(applePayValue);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      apiErrorToast(t('clients.smthWentWrong'), error);
    }
  };

  const handleCheckboxClick = ({ target: { name } }) => {
    if (appMode[name] === 'DISABLED') {
      setAppMode(prevState => ({
        ...prevState,
        [name]: name === 'catering' ? 'FULL' : 'DISHES',
      }));
    } else if (name === 'override') {
      setAppMode(prevState => ({
        ...prevState,
        [name]: !appMode[name],
      }));
    } else {
      setAppMode(prevState => ({
        ...prevState,
        [name]: 'DISABLED',
      }));
    }
  };

  const handleAppearanceOverrideClick = () => {
    if (
      shouldPrefillAppearance(isAppearanceOverridden, isAppearancePrefilled)
    ) {
      setKeyAppearance(brandAppearance);
      setIsAppearancePrefilled(true);
    }

    setIsAppearanceOverridden(prevState => !prevState);
  };

  const handleKeyAppearanceChange = moduleName => module => {
    setKeyAppearance(prevState => ({ ...prevState, [moduleName]: module }));
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const isApplePayChanged =
        applePayDomainAssociation !== initialApplePayDomainAssociation;
      const hasApplePayValue = applePayDomainAssociation.trim() !== '';
      const payload = buildAccessKeyPayload({
        name: accessKeyName,
        domain,
        appMode,
        keyAppearance,
        isAppearanceOverridden,
        isAppearanceAvailable: isShopOnlyAppMode(
          getEffectiveAppMode(appMode, brandAppMode)
        ),
      });
      await put(`/brand-access-keys/${id}`, {
        ...payload,
        overriddenConfiguration: {
          ...payload.overriddenConfiguration,
          ...(isApplePayChanged && {
            PayU: {
              override: hasApplePayValue,
              applePayDomainAssociation: hasApplePayValue
                ? applePayDomainAssociation
                : null,
            },
          }),
        },
      });
      toast.success(t('common.shared.savedChanges'));

      history.push('/admin/access-keys');
    } catch (error) {
      setIsLoading(false);
      apiErrorToast(t('errors.checkForm'), error);
    }
  };

  const effectiveAppMode = getEffectiveAppMode(appMode, brandAppMode);
  const displayedAppearance = isAppearanceOverridden
    ? keyAppearance
    : brandAppearance;

  return (
    <>
      <DialogLoader
        loaderState={isLoading}
        text={t('common.shared.isLoading')}
      />

      {appMode && !isLoading && (
        <>
          <CardWrapper title={t('brandCfg.accessKeys.keyConfiguration')}>
            <CardBody>
              <Divider className={classes.divider} />
              <GridContainer>
                <GridItem md={12}>
                  <FormTextInput
                    multiline={true}
                    label={t('columns.name')}
                    classes={classes}
                    name="name"
                    value={accessKeyName}
                    handleChange={ev => {
                      setAccessKeyName(ev.target.value);
                    }}
                    inputSize={6}
                    maxLength={400}
                  />
                </GridItem>
                <GridItem md={12}>
                  <FormTextInput
                    label={t('columns.domain')}
                    classes={classes}
                    name="domain"
                    value={domain}
                    handleChange={e => {
                      setDomain(e.target.value);
                    }}
                    inputSize={6}
                    maxLength={255}
                  />
                </GridItem>
                <GridItem md={12}>
                  <FormTextInput
                    label={t('brandCfg.accessKeys.applePayDomainAssociation')}
                    classes={classes}
                    name="applePayDomainAssociation"
                    value={applePayDomainAssociation}
                    handleChange={e => {
                      setApplePayDomainAssociation(e.target.value);
                    }}
                    inputSize={6}
                    maxLength="none"
                    multiline={true}
                    rows={4}
                    rowsMax={10}
                  />
                </GridItem>
                <GridItem xs={12} lg={6}>
                  <h3>{t('brandCfg.operatingMode.formOfBusinessOrSale')}</h3>
                </GridItem>
                <GridItem xs={12} lg={6}>
                  <div className={classes.overrideWrapper}>
                    <div className={classes.checkboxWrapper}>
                      <RegularCheckbox
                        name="override"
                        onClick={handleCheckboxClick}
                        checked={appMode?.override}
                      />
                      <span>{t('brandCfg.accessKeys.appMode.override')}</span>
                    </div>
                  </div>
                </GridItem>
                <GridItem xs={12}>
                  <div className={classes.checkboxWrapper}>
                    <RegularCheckbox
                      name="catering"
                      onClick={handleCheckboxClick}
                      checked={appMode.catering === 'FULL'}
                      disabled={!appMode.override}
                    />
                    <span>{t('brandCfg.operatingMode.dietCatering')}</span>
                  </div>

                  <div className={classes.radioWrapper}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={appMode.catering === 'FULL'}
                          onChange={() => {
                            setAppMode(prevState => ({
                              ...prevState,
                              catering: 'FULL',
                            }));
                          }}
                          icon={
                            <FiberManualRecord
                              className={classes.radioUnchecked}
                            />
                          }
                          checkedIcon={
                            <FiberManualRecord
                              className={classes.radioChecked}
                            />
                          }
                          classes={{
                            checked: classes.radio,
                            root: classes.radioRoot,
                          }}
                          disabled={!appMode.override}
                        />
                      }
                      classes={{
                        label: classes.label,
                      }}
                      label={
                        <>
                          {t('brandCfg.operatingMode.fullVersion')}

                          <InfoIconTooltip
                            title={
                              <h5
                                dangerouslySetInnerHTML={{
                                  __html: t(
                                    'brandCfg.operatingMode.fullVersionTooltip',
                                    {
                                      interpolation: {
                                        escapeValue: false,
                                      },
                                    }
                                  ),
                                }}
                              />
                            }
                          />
                        </>
                      }
                    />
                  </div>
                </GridItem>

                <GridItem xs={12}>
                  <div className={classes.checkboxWrapper}>
                    <RegularCheckbox
                      name="shop"
                      onClick={handleCheckboxClick}
                      checked={appMode.shop === 'DISHES'}
                      disabled={!appMode.override}
                    />
                    <span>
                      {t('brandCfg.operatingMode.individualMealSale')}
                    </span>
                  </div>

                  <div className={classes.radioWrapper}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={appMode.shop === 'DISHES'}
                          onChange={() => {
                            setAppMode(prevState => ({
                              ...prevState,
                              shop: 'DISHES',
                            }));
                          }}
                          icon={
                            <FiberManualRecord
                              className={classes.radioUnchecked}
                            />
                          }
                          checkedIcon={
                            <FiberManualRecord
                              className={classes.radioChecked}
                            />
                          }
                          classes={{
                            checked: classes.radio,
                            root: classes.radioRoot,
                          }}
                          disabled={!appMode.override}
                        />
                      }
                      classes={{
                        label: classes.label,
                      }}
                      label={
                        <>
                          {t('brandCfg.operatingMode.shopMeals')}
                          <InfoIconTooltip
                            title={
                              <h5
                                dangerouslySetInnerHTML={{
                                  __html: t(
                                    'brandCfg.operatingMode.shopMealsTooltip',
                                    {
                                      interpolation: {
                                        escapeValue: false,
                                      },
                                    }
                                  ),
                                }}
                              />
                            }
                          />
                        </>
                      }
                    />
                  </div>
                </GridItem>
              </GridContainer>
            </CardBody>
          </CardWrapper>
          {isShopOnlyAppMode(effectiveAppMode) && (
            <CardWrapper
              title={t('brandCfg.accessKeys.shopPanelAppearance')}
              additionalContentAfterTitle={
                <div className={classes.cardTitleOverride}>
                  <RegularCheckbox
                    name="appearanceOverride"
                    onClick={handleAppearanceOverrideClick}
                    checked={isAppearanceOverridden}
                  />
                  <span>{t('brandCfg.accessKeys.appMode.override')}</span>
                </div>
              }
            >
              <CardBody>
                <Divider className={classes.divider} />
                <BackgroundSettingsFields
                  t={t}
                  module={displayedAppearance.BrandColors}
                  onChange={handleKeyAppearanceChange('BrandColors')}
                  classes={classes}
                  isColor={isColor}
                  setIsColor={setIsColor}
                  disabled={!isAppearanceOverridden}
                />
                <div className={classes.bannerFields}>
                  <BannerSettingsFields
                    t={t}
                    module={displayedAppearance.OrderFormBanner}
                    onChange={handleKeyAppearanceChange('OrderFormBanner')}
                    disabled={!isAppearanceOverridden}
                    isStacked={true}
                  />
                </div>
              </CardBody>
            </CardWrapper>
          )}
          <FormControlButtons
            classes={classes}
            submitText={t('common.shared.save')}
            handleSubmit={handleSubmit}
          />
        </>
      )}
    </>
  );
};

export default AccessKeyEditModule;
